/**
 * Fact Finder API Service
 * Handles communication with the backend fact finder endpoints
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function request(path, options = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      ...getAuthHeaders(),
      ...(options.headers || {}),
    },
    ...options,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || data.detail || 'Request failed');
  }

  return data;
}

/**
 * Analyze a case using AI
 */
export async function analyzeIncident(caseData) {
  return request('/api/fact-finder-v2/analyze', {
    method: 'POST',
    body: JSON.stringify({ case_data: caseData }),
  });
}

/**
 * Save a case draft to the database
 */
export async function saveCaseDraft(payload) {
  return request('/api/fact-finder-v2/cases', {
    method: 'POST',
    body: JSON.stringify({
      case_data: payload.caseData,
      analysis: payload.analysis || null,
    }),
  });
}

/**
 * Get a list of user's cases
 */
export async function listCases(limit = 20, skip = 0) {
  return request(`/api/fact-finder-v2/cases?limit=${limit}&skip=${skip}`, {
    method: 'GET',
  });
}

/**
 * Get a specific case by ID
 */
export async function getCase(caseId) {
  return request(`/api/fact-finder-v2/cases/${caseId}`, {
    method: 'GET',
  });
}

/**
 * Update an existing case
 */
export async function updateCase(caseId, payload) {
  return request(`/api/fact-finder-v2/cases/${caseId}`, {
    method: 'PUT',
    body: JSON.stringify({
      case_data: payload.caseData,
      analysis: payload.analysis || null,
    }),
  });
}

/**
 * Delete a case
 */
export async function deleteCase(caseId) {
  return request(`/api/fact-finder-v2/cases/${caseId}`, {
    method: 'DELETE',
  });
}

/**
 * Upload evidence file
 */
export async function uploadEvidence(file, evidenceType = 'document', description = '') {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('evidence_type', evidenceType);
  formData.append('description', description);

  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/api/fact-finder-v2/upload-evidence`, {
    method: 'POST',
    headers,
    body: formData,
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || data.detail || 'Upload failed');
  }
  return data;
}

/**
 * Check what information is missing from a case
 */
export async function checkMissingInfo(caseData) {
  return request('/api/fact-finder-v2/missing-info', {
    method: 'POST',
    body: JSON.stringify(caseData),
  });
}

/**
 * Quick AI analysis for Quick Log entries
 */
export async function quickAnalyze(narrative) {
  return request('/api/fact-finder-v2/quick-analyze', {
    method: 'POST',
    body: JSON.stringify({ narrative }),
  });
}

/**
 * Transcribe audio using OpenAI Whisper
 */
export async function transcribeAudio(audioBlob) {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('file', audioBlob, 'recording.webm');

  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/api/fact-finder-v2/transcribe`, {
    method: 'POST',
    headers,
    body: formData,
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || data.detail || 'Transcription failed');
  }
  return data;
}


// =====================
// P4 — Fact Finder Enhancements
// =====================

/**
 * Anonymize text — strip names, IDs, addresses
 */
export async function anonymizeText(text) {
  return request('/api/fact-finder-v2/anonymize', {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

/**
 * Detect management strategy from case data
 */
export async function detectStrategy(caseData) {
  return request('/api/fact-finder-v2/strategy-detect', {
    method: 'POST',
    body: JSON.stringify({ case_data: caseData }),
  });
}

/**
 * Generate meeting prep guide from case data
 */
export async function generateMeetingPrep(caseData) {
  return request('/api/fact-finder-v2/meeting-prep', {
    method: 'POST',
    body: JSON.stringify({ case_data: caseData }),
  });
}

/**
 * Generate response style guide
 */
export async function generateResponseStyle(caseData, style) {
  return request('/api/fact-finder-v2/response-style', {
    method: 'POST',
    body: JSON.stringify({ case_data: caseData, style }),
  });
}
